import React, {type ReactNode, useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {ProfileTabStructure} from 'src/components/profile-tab-structure';
import {Skeleton, SkeletonItem} from 'src/components/skeleton';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {
    clearAccountLicense,
    isAccountUntracked,
    selectAccountDetails,
} from 'src/modules/profile/account/services/account-page-slice';
import {NoLicenseSidebarContent} from 'src/modules/profile/account/tabs/license-and-features/no-license-page/no-license-page';
import {getAccountLicenseHistory} from 'src/openapi/admin/apis/GetAccountLicenseHistoryApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group, Item} from '@pb/kit-component-group';
import {SecondaryTabs, Tab} from '@pb/kit-component-tabs-v2';


import {LICENSE_AND_FEATURES_SUB_TABS} from '../constants';
import {clearAccountLicenseHistory, selectAccountLicenseHistory} from '../services/account-license-features-slice';

import {LicenseSuggest} from './components/license-suggest';
import {AccountBilling} from './sections/account-billing';
import {LicenseInformation} from './sections/license-information';
import {LicenseNotes} from './sections/license-notes';

import {
    basicLicenceTabStructure,
    basicLicenceTabStructureContent,
    basicLicenceTabStructureHeader,
} from './basic-licence-tab-structure.module.styl';


type BaseProps = {
    main: ReactNode,
    accountId: number,
    activeSubTab: string,
};

type WithLicenseProps = BaseProps & {
    noLicense?: false,
    licenseId: number,
};

type NoLicenseProps = BaseProps & {
    noLicense: true,
    licenseId?: never,
};

type Props = WithLicenseProps | NoLicenseProps;

export const BasicLicenseTabStructure = (props: Props) => {
    const {main, accountId, activeSubTab, noLicense} = props;

    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {hasAccess, canViewAccountConnectorLimits} = useAdminAccessRight();
    const {accountTabChange} = useEventTracking();

    const isUntracked = useSelector(isAccountUntracked);
    const {hierarchy} = useSelector(selectAccountDetails);
    const licenseHistory = useSelector(selectAccountLicenseHistory);

    const isReportsAllowed = hasAccess(ACCESS_RIGHTS.ACCOUNT_REPORTS_SCREEN_VIEW);
    const isSsoAllowed = useMemo(() => (
        hasAccess(ACCESS_RIGHTS.SSO_SCREEN_VIEW) && hierarchy?.current?.entityTypeCode !== 'Event'
    ), [hierarchy, hasAccess]);
    const isConnectorLimitsAllowed = canViewAccountConnectorLimits(accountId);

    const tabDisabled = Boolean(noLicense);

    useEffect(() => {
        if (accountId && !noLicense) {
            apiDispatch(getAccountLicenseHistory({accountId: +accountId}));
        }
    }, [accountId, apiDispatch, noLicense]);

    const onSubTabChange = useCallback((subTab: string) => {
        if (props.noLicense) {
            if (subTab === LICENSE_AND_FEATURES_SUB_TABS.CONNECTOR_LIMITS.name) {
                accountTabChange({activeTab: subTab, previousTab: activeSubTab});
                navigate(`/profile/account/${accountId}/license-features/${subTab}`);
            }
            return;
        }

        if (subTab === LICENSE_AND_FEATURES_SUB_TABS.CRM.name && isUntracked) {
            return;
        }

        if (subTab === LICENSE_AND_FEATURES_SUB_TABS.REPORTS.name && isUntracked) {
            return;
        }

        accountTabChange({activeTab: subTab, previousTab: activeSubTab});
        navigate(`/profile/account/${accountId}/license-features/${props.licenseId}/${subTab}`);
    }, [props, accountId, isUntracked, navigate, activeSubTab, accountTabChange]);


    useEffect(() => {
        return () => {
            dispatch(clearAccountLicenseHistory());
            dispatch(clearAccountLicense());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const sidebar = props.noLicense
        ? [<NoLicenseSidebarContent key="no-license" accountId={accountId}/>]
        : [
                <ValidationContextProvider key="license-information-section">
                    <LicenseInformation licenseId={props.licenseId} accountId={accountId}/>
                </ValidationContextProvider>,
                <ValidationContextProvider key="account-billing">
                    <AccountBilling
                        key="account-billing-section"
                        licenseId={props.licenseId}
                        accountId={accountId}
                    />
                </ValidationContextProvider>,
                <LicenseNotes
                    key={`license-notes-section ${props.licenseId}`}
                    licenseId={props.licenseId}
                    accountId={accountId}
                />,
            ];

    return (
        <div className={basicLicenceTabStructure}>
            <Group className={basicLicenceTabStructureHeader}>
                {noLicense
                    ? <div style={{width: 448}}/>
                    : (
                            <Group style={{width: 448, height: '100%'}} alignItems="center">
                                <Item style={{width: 55}}>
                                    <b>License:</b>
                                </Item>
                                <Item>
                                    <Skeleton view={<SkeletonItem width={180} height={32}/>} shown={!licenseHistory}>
                                        <LicenseSuggest history={licenseHistory} activeSubTab={activeSubTab}/>
                                    </Skeleton>
                                </Item>
                            </Group>
                        )}
                <SecondaryTabs value={activeSubTab} onChange={onSubTabChange}>
                    {[
                        <Tab
                            key="FeaturesLimitsTab"
                            disabled={tabDisabled}
                            value={LICENSE_AND_FEATURES_SUB_TABS.FEATURES_AND_LIMITS.name}
                            caption={LICENSE_AND_FEATURES_SUB_TABS.FEATURES_AND_LIMITS.caption}
                        />,
                        ...hasAccess(ACCESS_RIGHTS.CRM_SCREEN_VIEW) ? [
                            <Tab
                                key="CRMTab"
                                disabled={isUntracked || tabDisabled}
                                value={LICENSE_AND_FEATURES_SUB_TABS.CRM.name}
                                caption={LICENSE_AND_FEATURES_SUB_TABS.CRM.caption}
                            />,
                        ] : [],
                        ...isSsoAllowed
                            ? [
                                    <Tab
                                        key="SSOTab"
                                        disabled={tabDisabled}
                                        value={LICENSE_AND_FEATURES_SUB_TABS.SSO.name}
                                        caption={LICENSE_AND_FEATURES_SUB_TABS.SSO.caption}
                                    />,
                                ] : [],
                        ...isReportsAllowed
                            ? [
                                    <Tab
                                        key="ReportsTab"
                                        disabled={isUntracked || tabDisabled}
                                        value={LICENSE_AND_FEATURES_SUB_TABS.REPORTS.name}
                                        caption={LICENSE_AND_FEATURES_SUB_TABS.REPORTS.caption}
                                    />,
                                ] : [],
                        ...isConnectorLimitsAllowed ? [
                            <Tab
                                key="ConnectorLimitsTab"
                                value={LICENSE_AND_FEATURES_SUB_TABS.CONNECTOR_LIMITS.name}
                                caption={LICENSE_AND_FEATURES_SUB_TABS.CONNECTOR_LIMITS.caption}
                            />,
                        ] : [],
                    ]}
                </SecondaryTabs>
            </Group>
            <div className={basicLicenceTabStructureContent}>
                <ProfileTabStructure
                    width={488}
                    minWidth={488}
                    main={main}
                    sidebar={sidebar}
                />
            </div>
        </div>
    );
};
